/**
 * HANDY 실시간 시세 + 차트 카드 (2026-10-01, dev)
 *
 * MEXC 실거래(HANDY/USDT)를 백엔드 프록시로 받는다. 차트는 SVG.
 * 팝업은 색·여백을 인라인으로 고정해 어떤 테마에서도 동일하게 보인다.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { X } from 'lucide-react';

type Market = { price: number; changePct: number; high: number; low: number };
type Candle = { t: number; c: number };

const UP = '#16A34A', DOWN = '#DC2626';
const fmt = (v: number) => '$' + v.toFixed(v < 0.01 ? 5 : 4);
const kday = (t: number) =>
  new Date(t + 9 * 3600 * 1000).toISOString().slice(5, 10).replace('-', '.');

function Spark({ candles, color, height = 64, labels = false }:
  { candles: Candle[]; color: string; height?: number; labels?: boolean }) {
  if (!candles.length) return <div style={{ height }} />;
  const vals = candles.map(c => c.c);
  const min = Math.min(...vals), max = Math.max(...vals);
  const W = 320, H = height, pad = 4;
  const x = (i: number) => pad + (W - pad * 2) * i / Math.max(1, vals.length - 1);
  const y = (v: number) => max === min ? H / 2 : pad + (H - pad * 2) * (1 - (v - min) / (max - min));
  const pts = vals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${pad},${H - pad} ${pts} ${W - pad},${H - pad}`;
  return (
    <div>
      {labels && (
        <div className="flex justify-between" style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
          <span>최저 {fmt(min)}</span><span>최고 {fmt(max)}</span>
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height, display: 'block' }} preserveAspectRatio="none">
        <polygon points={area} fill={color} opacity="0.08" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </div>
  );
}

const RANGES = [['24h', '24H'], ['7d', '7D'], ['30d', '30D']] as const;

export function PriceChartCard() {
  const [mk, setMk] = useState<Market | null>(null);
  const [spark, setSpark] = useState<Candle[]>([]);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [big, setBig] = useState<Candle[]>([]);
  const [daily, setDaily] = useState<Candle[]>([]);
  const [links, setLinks] = useState<{ mexc: string; dex: string }>({ mexc: '', dex: '' });

  // MEXC UID 이벤트가 실제로 켜져 있는지. 꺼져 있으면 버튼을 띄우지 않는다.
  const [mexcOn, setMexcOn] = useState(false);
  useEffect(() => {
    const t = localStorage.getItem('handy-jwt-token');
    if (!t) return;
    fetch('/api/mexc/status', { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.json())
      .then(j => setMexcOn(Boolean(j?.enabled)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const m = await fetch('/api/price/handy/market').then(r => r.json());
        if (m?.price) setMk({ price: m.price, changePct: m.changePct, high: m.high, low: m.low });
        const k = await fetch('/api/price/handy/klines?range=7d').then(r => r.json());
        if (k?.success) setSpark(k.candles);
        const L = await fetch('/api/price/handy/links').then(r => r.json());
        if (L?.success) setLinks({ mexc: L.mexc || '', dex: L.dex || '' });
      } catch { /* 시세 실패가 홈을 막으면 안 된다 */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const k = await fetch(`/api/price/handy/klines?range=${range}`).then(r => r.json());
        if (k?.success) setBig(k.candles);
      } catch {}
    })();
  }, [open, range]);

  useEffect(() => {
    if (!open || daily.length) return;
    (async () => {
      try {
        const k = await fetch('/api/price/handy/klines?range=30d').then(r => r.json());
        if (k?.success) setDaily(k.candles);
      } catch {}
    })();
  }, [open]);

  const up = (mk?.changePct ?? 0) >= 0;
  const color = up ? UP : DOWN;
  const week = daily.slice(-8);

  return (
    <>
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-sm text-muted-foreground">HANDY 실시간 시세</p>
              <p className="text-2xl font-bold" style={{ marginTop: 2 }}>
                {mk ? fmt(mk.price) : '조회 중…'}
              </p>
            </div>
            {mk && (
              <span className="text-sm font-bold px-2 py-1 rounded text-white"
                style={{ background: color }}>
                {up ? '▲' : '▼'} {Math.abs(mk.changePct).toFixed(2)}% (24h)
              </span>
            )}
          </div>
          <Spark candles={spark} color={color} />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {mk ? `24h 고가 ${fmt(mk.high)} · 저가 ${fmt(mk.low)}` : ''}
            </p>
            <button onClick={() => setOpen(true)} className="text-sm font-bold"
              style={{ color: '#FF6B00' }}>
              크게 보기 ›
            </button>
          </div>
          {/* [2026-10-17] 월렛구매·MEXC거래 버튼을 가린다.
              '월렛에서 구매' 는 지갑 탭으로 이동만 시키고 구매창을 열지 않아서,
              눌러도 어디서 뭘 해야 하는지 알 수 없는 상태였다. 구매 화면을
              브릿지처럼 제대로 만들기 전까지는 띄우지 않는다. */}
          {false && (
          <div className="flex" style={{ gap: 8, marginTop: 12 }}>
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent('handy:go-buy')); } catch {} }}
              className="flex-1 text-center font-bold text-sm"
              style={{ background: '#FF6B00', color: '#fff', padding: '12px 0',
                       borderRadius: 12, border: 0 }}>
              월렛에서 구매
            </button>
            {links.mexc && (
              <a href={links.mexc} target="_blank" rel="noopener noreferrer"
                className="flex-1 text-center font-bold text-sm"
                style={{ background: '#2563EB', color: '#fff', padding: '12px 0', borderRadius: 12 }}>
                MEXC에서 거래
              </a>
            )}
          </div>
          )}
          {/* [2026-10-17] mexc_event_enabled 가 false 인데 버튼은 떠 있었다.
              눌러서 UID 를 넣어도 6,000 wHANDY 를 받을 수 없는 상태였다.
              이벤트가 켜져 있을 때만 띄운다. */}
          {links.mexc && mexcOn && (
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent('handy:go-rewards')); } catch {} }}
              className="w-full text-center text-sm font-semibold mt-2"
              style={{ color: '#2563EB', background: 'rgba(37,99,235,.08)',
                       padding: '10px 0', borderRadius: 10, border: 0 }}>
              MEXC 가입했다면 → UID 입력하고 6,000 wHANDY 받기 ›
            </button>
          )}
        </CardContent>
      </Card>

      {open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,.6)', padding: '16px 20px 110px' }}>
          <div className="w-full flex flex-col"
            style={{ background: '#FFFFFF', color: '#111827', maxWidth: 420,
                     maxHeight: '70vh', borderRadius: 20, overflow: 'hidden' }}>

            {/* 헤더 */}
            <div className="flex items-center justify-between"
              style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 18, fontWeight: 700 }}>HANDY/USDT 차트</h3>
              <button onClick={() => setOpen(false)} aria-label="닫기"
                className="flex items-center justify-center"
                style={{ width: 36, height: 36, borderRadius: 999, background: '#111827', flexShrink: 0 }}>
                <X className="h-5 w-5" style={{ color: '#FFFFFF' }} />
              </button>
            </div>

            {/* 본문 (스크롤) */}
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              {mk && (
                <div className="flex items-center" style={{ gap: 12, marginBottom: 16 }}>
                  <p style={{ fontSize: 30, fontWeight: 700 }}>{fmt(mk.price)}</p>
                  <span style={{ background: color, color: '#fff', fontSize: 14,
                                 fontWeight: 700, padding: '4px 10px', borderRadius: 8 }}>
                    {up ? '▲' : '▼'} {Math.abs(mk.changePct).toFixed(2)}%
                  </span>
                </div>
              )}

              <div className="flex" style={{ gap: 8, marginBottom: 16 }}>
                {RANGES.map(([k, label]) => (
                  <button key={k} onClick={() => setRange(k)}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 12, fontSize: 14, fontWeight: 700,
                             ...(range === k
                               ? { background: '#FF6B00', color: '#fff' }
                               : { background: '#f3f4f6', color: '#6b7280' }) }}>
                    {label}
                  </button>
                ))}
              </div>

              <Spark candles={big} color={color} height={160} labels />

              {week.length > 1 && (
                <div style={{ marginTop: 24 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>최근 1주일 시세</p>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
                    {week.slice(1).reverse().map((c, idx, arr) => {
                      const i = week.length - 1 - idx;
                      const prev = week[i - 1];
                      const ch = prev ? (c.c - prev.c) / prev.c * 100 : 0;
                      return (
                        <div key={c.t} className="flex items-center justify-between"
                          style={{ padding: '11px 16px', fontSize: 14,
                                   ...(idx < arr.length - 1 ? { borderBottom: '1px solid #f3f4f6' } : {}) }}>
                          <span style={{ color: '#6b7280', width: 52 }}>{kday(c.t)}</span>
                          <span style={{ fontWeight: 600 }}>{fmt(c.c)}</span>
                          <span style={{ fontWeight: 700, color: ch >= 0 ? UP : DOWN,
                                         width: 76, textAlign: 'right' }}>
                            {ch >= 0 ? '+' : ''}{ch.toFixed(2)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 14 }}>
                MEXC 거래소 실거래 가격 · 1분마다 갱신
              </p>
            </div>

            {/* 푸터 */}
            <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => setOpen(false)}
                style={{ width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 16,
                         fontWeight: 700, background: '#111827', color: '#FFFFFF' }}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
